<template>
    <div class="bulk-return-wrap">
        <div class="px-2">
            <AggregatedProductReturnTable :tableItems="tableItems"/>
        </div>

        <div class="row px-2 mb-2">
            <div class="col-12">
                <div class="summary-bar d-flex flex-wrap align-items-center gap-3" :class="{ 'summary-bar-invalid': hasInvalidQty }">
                    <span><strong>{{ selectedCount }}</strong> item(s) selected</span>
                    <span>Total return qty: <strong>{{ selectedQty }}</strong></span>
                    <span>Approx. return amount: <strong>{{ approxAmount }}</strong></span>
                    <span v-if="hasInvalidQty" class="text-danger small">Fix the highlighted return quantities</span>
                    <span class="text-muted small">Actual amount uses each bill's own rate</span>
                </div>
            </div>
        </div>

        <div class="row px-2">
            <div class="col-md-6">
                <div class="mb-1">
                    <label class="form-label" for="description">Ledger Memo</label>
                    <textarea
                        id="description"
                        v-model="formData.description"
                        class="form-control"
                    />
                </div>
            </div>
            <div class="col-md-5 ml-auto mt-auto mb-1">
                <div class="d-flex flex-wrap gap-1 gy-2">
                    <button @click="handleSubmit" :disabled="! selectedCount || hasInvalidQty" class="btn btn-primary">
                        {{ submitLabel }}
                    </button>
                </div>
            </div>
        </div>

        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import { computed, inject, onMounted, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import AggregatedProductReturnTable from '@/components/molecule/company/hospital/ipd-pharmacy-bill/AggregatedProductReturnTable.vue';
import Loader from '@/components/atom/LoaderComponent';
import handleHospitalBilling from '@/services/modules/hospital/billing';
import useDate from '@/services/utils/day';
import figureFormatter from '@/services/utils/figureFormatter';

const props = defineProps({
    ipdRegisterId: {
        type: [String, Number],
        required: true
    }
})

const emit = defineEmits(['onSaved']);

const $route = useRoute();
const { fetchIpdReturnableProducts, bulkReturnBillings } = handleHospitalBilling();
const { currentDate } = useDate();
const { commaFormat } = figureFormatter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');

const loading = ref(false);
const tableItems = reactive([]);

const formData = ref({
    description: '',
    date: currentDate()
});

const selectedItems = computed(() => tableItems.filter(item => item.return_qty > 0));

const selectedCount = computed(() => selectedItems.value.length);

const selectedQty = computed(() => selectedItems.value.reduce((sum, item) => sum + Number(item.return_qty), 0));

const approxAmount = computed(() => {
    const total = selectedItems.value.reduce((sum, item) => sum + (Number(item.return_qty) * Number(item.average_rate)), 0);
    if (! total) {
        return '0.00';
    }
    return commaFormat(total);
});

const hasInvalidQty = computed(() => tableItems.some(item => item.return_qty > item.returnable_quantity));

const submitLabel = computed(() => {
    if (! selectedCount.value) {
        return 'Return Selected Items';
    }
    return `Return ${selectedCount.value} Selected Item(s)`;
});

const formatReturnableItems = (items) => {
    return items.map(item => {
        return { ...item, return_qty: 0 };
    });
}

const loadReturnableProducts = () => {
    if (! props.ipdRegisterId) {
        return showError('IPD register is missing');
    }
    loading.value = true;
    const query = `?company_id=${$route.params.companyId}`;
    return fetchIpdReturnableProducts(props.ipdRegisterId, query).then((res) => {
        if (! res.status) {
            return showError(res.message);
        }
        tableItems.splice(0, tableItems.length, ...formatReturnableItems(res.data));
    }).finally(() => {
        loading.value = false;
    });
}

onMounted(() => {
    loadReturnableProducts();
})

const handleSubmit = () => {
    if (hasInvalidQty.value) {
        return showError('Return qty cannot be greater than returnable qty for one or more items');
    }

    if (! selectedItems.value.length) {
        return showError('Please enter return quantity for at least one item');
    }

    const confirmed = window.confirm(`Return ${selectedCount.value} item(s) with total qty ${selectedQty.value} (approx. amount ${approxAmount.value})?`);

    if (! confirmed) {
        return;
    }

    const itemDetails = selectedItems.value.map(item => {
        return {
            product_id: item.product_id,
            quantity: item.return_qty,
            description: item.product_name
        };
    });

    loading.value = true;

    const payload = {
        company_id: $route.params.companyId,
        ipd_register_id: props.ipdRegisterId,
        description: formData.value.description,
        date: formData.value.date,
        item_details: itemDetails
    };

    bulkReturnBillings(payload).then((res) => {
        if (! res.status) {
            loading.value = false;
            return showError(res.message);
        }
        showSuccess(res.message);
        emit('onSaved');
        return loadReturnableProducts();
    }).catch(() => {
        loading.value = false;
    });
}
</script>

<style scoped>
.bulk-return-wrap {
    padding-top: 0;
}

.summary-bar {
    position: sticky;
    bottom: 0;
    z-index: 3;
    background-color: #f0f6fe;
    border: 1px solid rgba(13, 110, 253, 0.15);
    border-radius: 6px;
    padding: 0.6rem 1rem;
}

.summary-bar-invalid {
    background-color: #fdf3f3;
    border-color: rgba(220, 53, 69, 0.4);
}
</style>
